import { Waves, ArrowUpRight } from "lucide-react";
import { useLocation } from "react-router-dom";
import { useSimplify } from "./simplify-context";
import { FAQS } from "./FAQSection";

const NAV = [
  { label: "Solutions", href: "/solutions" },
  { label: "Live demos", href: "/demos" },
  { label: "Pricing", href: "/pricing" },
  { label: "How it works", href: "/how-it-works" },
  { label: "About", href: "/about" },
  { label: "Journal", href: "/journal" },
];

const MORE = [
  { label: "Get started", href: "/get-started" },
  { label: "For construction", href: "/industries/construction" },
  { label: "For professional services", href: "/industries/professional-services" },
  { label: "For real estate", href: "/industries/real-estate" },
  { label: "For consulting", href: "/industries/consulting" },
  { label: "Trust & guardrails", href: "/trust" },
  { label: "Gardez vos outils (FR)", href: "/fr/gardez-vos-outils" },
  { label: "Loi 25 & vos données (FR)", href: "/fr/loi-25-donnees-clients" },
  { label: "RFI en quelques heures (FR)", href: "/fr/reponse-rfi-quelques-heures" },
  { label: "Accueil français (FR)", href: "/fr" },
  { label: "Audit gratuit 15 min (FR)", href: "/fr/audit-gratuit-15-minutes" },
  { label: "Inicio en español (ES)", href: "/es" },
  { label: "Mantenga sus herramientas (ES)", href: "/es/mantenga-sus-herramientas" },
  { label: "Tarifs (FR)", href: "/fr/tarifs" },
  { label: "Precios (ES)", href: "/es/precios" },
  { label: "Solutions (FR)", href: "/fr/solutions" },
  { label: "Soluciones (ES)", href: "/es/soluciones" },
  { label: "Zoho integrations Montréal", href: "/zoho-integrations-montreal" },
];

const orgSchema = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      "@id": "https://oceandata.ca/#org",
      name: "Ocean Data",
      alternateName: "Dataflow Solution",
      url: "https://oceandata.ca",
      slogan: "More capacity. Better work.",
      description:
        "Ocean Data gives every person the information they need to spend more time doing what they're best at. We connect the tools construction and professional-services businesses already use into one simple system — 647 Zoho One implementations since 2017, with 25–40% less administrative time measured across client engagements.",
      founder: {
        "@type": "Person",
        name: "Bruno Pouliot",
        jobTitle: "Founder & Principal Consultant",
        sameAs: "https://www.linkedin.com/in/bruno-pouliot-5a992535/",
      },
      sameAs: [
        "https://oceandata.ca",
        "https://dataflowsolution.com",
        "https://www.linkedin.com/in/bruno-pouliot-5a992535/",
      ],
      hasOfferCatalog: {
        "@type": "OfferCatalog",
        name: "Business simplification services",
        itemListElement: [
          {
            "@type": "Offer",
            name: "Free 15-minute business review",
            price: "0",
            priceCurrency: "CAD",
            description:
              "A free, low-pressure review of where your team's hours are going — the entry point to every engagement.",
          },
          {
            "@type": "Offer",
            name: "Quick Setup — CRM and marketing integration on your existing tools",
            price: "899",
            priceCurrency: "USD",
          },
          {
            "@type": "Offer",
            name: "Project — one critical process, flowing",
            price: "1799",
            priceCurrency: "USD",
          },
          {
            "@type": "Offer",
            name: "Enterprise — sales, projects, and money as one system",
            price: "3699",
            priceCurrency: "USD",
          },
          {
            "@type": "Offer",
            name: "Custom Implementation — one simple system, architected for you",
            price: "4779",
            priceCurrency: "USD",
            description: "For larger organisations with more moving parts.",
          },
        ],
      },
    },
    {
      "@type": ["ProfessionalService", "LocalBusiness"],
      "@id": "https://oceandata.ca/#ocean-data-inc",
      name: "Ocean Data Inc.",
      parentOrganization: { "@id": "https://oceandata.ca/#org" },
      telephone: "+1-541-446-9447",
      address: {
        "@type": "PostalAddress",
        streetAddress: "75 Rue Queen Ste 1400",
        addressLocality: "Montréal",
        addressRegion: "QC",
        postalCode: "H3C 2N6",
        addressCountry: "CA",
      },
      areaServed: [
        { "@type": "City", name: "Montréal" },
        { "@type": "AdministrativeArea", name: "Québec" },
        { "@type": "Country", name: "Canada" },
      ],
      priceRange: "≈ $1,250–$6,600 CAD",
    },
    {
      "@type": ["ProfessionalService", "LocalBusiness"],
      "@id": "https://oceandata.ca/#ocean-data-llc",
      name: "Ocean Data LLC",
      parentOrganization: { "@id": "https://oceandata.ca/#org" },
      telephone: "+1-480-863-9046",
      address: {
        "@type": "PostalAddress",
        streetAddress: "300 W. Clarendon Ave Suite 325",
        addressLocality: "Phoenix",
        addressRegion: "AZ",
        postalCode: "85013",
        addressCountry: "US",
      },
      areaServed: [
        { "@type": "City", name: "Phoenix" },
        { "@type": "City", name: "Scottsdale" },
        { "@type": "AdministrativeArea", name: "Arizona" },
        { "@type": "City", name: "Miami" },
        { "@type": "City", name: "Fort Lauderdale" },
        { "@type": "AdministrativeArea", name: "Florida" },
        { "@type": "Country", name: "United States" },
      ],
      priceRange: "$899–$4,779 USD",
    },
    {
      "@type": "WebSite",
      "@id": "https://oceandata.ca/#website",
      url: "https://oceandata.ca",
      name: "Ocean Data",
      publisher: { "@id": "https://oceandata.ca/#org" },
      inLanguage: "en-CA",
    },
  ],
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const howToSchema = {
  "@context": "https://schema.org",
  "@type": "HowTo",
  name: "How to simplify business processes with Ocean Data",
  description:
    "A calm, three-step path from scattered tools to one source of truth for sales, projects, and books.",
  step: [
    {
      "@type": "HowToStep",
      position: 1,
      name: "Request a simplification review",
      text: "Tell us where your team's time is going. We map the repetitive steps standing between your people and their real work.",
    },
    {
      "@type": "HowToStep",
      position: 2,
      name: "We connect one source of truth",
      text: "We link your CRM, projects, and books into a single secure record, powered quietly by Claude copilots working through deep Zoho integration.",
    },
    {
      "@type": "HowToStep",
      position: 3,
      name: "Every role gets exactly its view",
      text: "Each person sees only what their role needs — sold versus delivered, today's priorities, plain answers to plain questions, reports without the scramble.",
    },
  ],
};

export default function Footer() {
  const { setOpen } = useSimplify();
  const { pathname } = useLocation();
  const locale = pathname.startsWith("/fr") ? "fr" : pathname.startsWith("/es") ? "es" : "en";
  const tagline = {
    en: { a: "More capacity.", b: "Better work." },
    fr: { a: "Plus de capacité.", b: "Meilleur travail." },
    es: { a: "Más capacidad.", b: "Mejor trabajo." },
  }[locale];
  const taglineBody = {
    en: "Ocean Data gives every person the information they need to spend more time doing what they're best at. One source of truth, clear responsibilities, less administrative work.",
    fr: "Ocean Data donne à chaque personne l'information dont elle a besoin pour passer plus de temps à faire ce qu'elle fait de mieux. Une seule source de vérité, des responsabilités claires, moins de travail administratif.",
    es: "Ocean Data le da a cada persona la información que necesita para dedicar más tiempo a lo que mejor sabe hacer. Una sola fuente de verdad, responsabilidades claras, menos trabajo administrativo.",
  }[locale];

  return (
    <footer data-testid="footer-container" className="bg-brand-navy text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-16 lg:py-24">
        <div className="grid grid-cols-12 gap-10 lg:gap-12 pb-14 border-b border-white/10">
          <div className="col-span-12 lg:col-span-6">
            <a href="/" className="flex items-center gap-2.5 mb-6">
              <span className="w-9 h-9 rounded-full bg-brand-teal/15 text-brand-pale flex items-center justify-center">
                <Waves size={17} strokeWidth={2.2} />
              </span>
              <span className="font-display font-bold text-lg tracking-tight">Ocean Data</span>
            </a>
            <p className="font-display font-semibold text-2xl sm:text-3xl leading-tight max-w-md mb-6">
              {tagline.a} <span className="text-brand-pale">{tagline.b}</span>
            </p>
            <p className="text-sm text-slate-400 leading-relaxed max-w-md">
              {taglineBody}
            </p>
          </div>

          <div className="col-span-6 lg:col-span-2">
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400 mb-5">Explore</p>
            <ul className="space-y-3">
              {NAV.map((n) => (
                <li key={n.href}>
                  <a href={n.href} className="text-sm text-slate-300 hover:text-brand-pale transition-colors duration-300">
                    {n.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="col-span-6 lg:col-span-2">
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400 mb-5">More</p>
            <ul className="space-y-3">
              {MORE.map((n) => (
                <li key={n.href}>
                  <a href={n.href} className="text-sm text-slate-300 hover:text-brand-pale transition-colors duration-300">
                    {n.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="col-span-6 lg:col-span-2">
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400 mb-5">Find us</p>
            <ul className="space-y-3 mb-8">
              <li>
                <a data-testid="footer-domain-oceandata" href="https://oceandata.ca" className="text-sm text-slate-300 hover:text-brand-pale transition-colors duration-300">
                  oceandata.ca
                </a>
              </li>
              <li>
                <a data-testid="footer-domain-dataflow" href="https://dataflowsolution.com" className="text-sm text-slate-300 hover:text-brand-pale transition-colors duration-300">
                  dataflowsolution.com
                </a>
              </li>
              <li>
                <a data-testid="footer-phone" href="tel:+15414469447" className="text-sm text-slate-300 hover:text-brand-pale transition-colors duration-300">
                  Ocean Data Inc. · +1.541.446.9447
                </a>
              </li>
              <li className="text-xs text-slate-500 leading-relaxed">
                75 Rue Queen Ste 1400, Montréal, QC H3C 2N6, Canada
              </li>
              <li>
                <a data-testid="footer-phone-us" href="tel:+14808639046" className="text-sm text-slate-300 hover:text-brand-pale transition-colors duration-300">
                  Ocean Data LLC · +1.480.863.9046
                </a>
              </li>
              <li className="text-xs text-slate-500 leading-relaxed">
                300 W. Clarendon Ave Suite 325, Phoenix, AZ 85013, United States
              </li>
            </ul>
            <button
              data-testid="footer-cta-button"
              onClick={() => setOpen(true)}
              className="inline-flex items-center gap-2 rounded-full bg-brand-teal text-white text-sm font-semibold px-6 py-3 transition-all duration-300 hover:bg-brand-tealhover hover:-translate-y-0.5"
            >
              Start simplifying
              <ArrowUpRight size={15} />
            </button>
          </div>
        </div>

        <div className="pt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <p className="text-xs text-slate-500">
            © {new Date().getFullYear()} Ocean Data · Dataflow Solution. All rights reserved.
          </p>
          <p className="text-xs text-slate-500">
            Connected to the tools you already use.
          </p>
        </div>
      </div>

      <script
        type="application/ld+json"
        data-testid="footer-seo-schema"
        dangerouslySetInnerHTML={{ __html: JSON.stringify([orgSchema, faqSchema, howToSchema]) }}
      />
    </footer>
  );
}
